import { forwardRef, memo } from "react";

import { VideoJsPlayerOptions, VideoPlayer, VideoPlayerHandle } from "@fronterahealth/frontera-ui-components";

interface VideoPlayerContentProps {
  videoUrl: string;
  onDurationChange?: (duration: number) => void;
  onSeeking?: () => void;
  onSeeked?: () => void;
  onTimeUpdate?: (time: number) => void;
  enabledFocus?: boolean;
  onPlayerReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
}

export const VideoPlayerContent = memo(
  forwardRef<VideoPlayerHandle, VideoPlayerContentProps>(
    (
      {
        videoUrl,
        onDurationChange,
        enabledFocus,
        onSeeked,
        onSeeking,
        onTimeUpdate,
        onPlayerReady,
        onPlay,
        onPause,
        onEnd,
      },
      ref,
    ) => {
      const videoPlayerOptions: VideoJsPlayerOptions = {
        sources: [{ src: videoUrl, type: "video/mp4" }],
        preload: "auto",
        fluid: true,
        autoplay: false,
        loop: false,
        muted: true,
        playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2],
        controlBar: {
          autoPlay: false,
          pictureInPictureToggle: true,
          playbackRateMenuButton: true,
          children: [
            "playToggle",
            "currentTimeDisplay",
            "timeDivider",
            "durationDisplay",
            "progressControl",
            "fullscreenToggle",
            "volumePanel",
            "skipBackward",
            "skipForward",
            "playbackRateMenuButton",
            "pictureInPictureToggle",
          ],
          progressControl: true,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          autoHide: false,
          skipButtons: { forward: 10, backward: 10 },
        },
        inactivityTimeout: 0,
        userActions: {
          click: false,
          doubleClick: true,
        },
        html5: {
          preloadTextTracks: true,
        },
      };

      return (
        <div className={`w-full aspect-video lg:rounded-t-2xl min-h-80 overflow-hidden`} data-video-player="true">
          <div className="video-sessions-player w-full h-full relative">
            <VideoPlayer
              ref={ref}
              src={videoUrl}
              onPlay={onPlay}
              onEnded={onEnd}
              onPause={onPause}
              onSeeked={onSeeked}
              onLoadedMetadata={(duration) => {
                onDurationChange?.(duration);
              }}
              onReady={onPlayerReady}
              onSeeking={onSeeking}
              onTimeUpdate={(state) => onTimeUpdate && onTimeUpdate(state.playedSeconds)}
              options={videoPlayerOptions}
              enableFocus={enabledFocus}
              className={`w-full! h-full! vjs-keep-controls vjs-user-active video-js vjs-default-skin`}
            />
          </div>
        </div>
      );
    },
  ),
);
